import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../main/components/Header";
import MobileBottomBar from "../../main/components/MobileBottomBar";
import Menu from "../components/Menu";
import WalletTopUp from "../components/WalletTopUp";
import BuyPackage from "../components/BuyPackage";
import PaymentHistory from "../components/PaymentHistory";
import Advertisements from "../components/Advertisements";
import Chats from "../components/Chats";
import Settings from "../components/Settings";
import Favorites from "../components/Favorites";
import ContractTemplates from "../components/ContractTemplates";
import CounterpartyCheck from "../components/CounterpartyCheck";
import Finance from "../components/Finance";
import MyOrders from "../components/MyOrders";
import MyReviewsContent from "../components/MyReviewsContent.js";
import DealerEmployees from "../components/DealerEmployees";
import Path from "../../path/ui/Path";

import { useTranslation } from "react-i18next";
import { useSEO } from "../../../hooks/useSEO";

function Main() {
    useSEO({
        title: 'Жеке кабинет — Agroland.kz',
        description: 'Пайдаланушы жеке кабинеті. Хабарламаларды басқару, баптаулар, төлемдер тарихы және сүйікті жарнамалар.',
        keywords: 'жеке кабинет, профиль, баптаулар, төлемдер, сүйіктілер',
        ogType: 'website',
        robots: 'noindex, nofollow' // Жеке кабинетті индекстеуге болмайды
    }, []);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState();
    const [hideBottomBar, setHideBottomBar] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);

    const goBack = useCallback(() => {
        navigate('/profile', { replace: true });
    }, [navigate]);

    const tabComponents = {
        walletTopUp: <WalletTopUp setActiveTab={goBack} />,
        purchasePackage: <BuyPackage setActiveTab={goBack} />,
        paymentHistory: <PaymentHistory setActiveTab={goBack} />,
        ads: <Advertisements setActiveTab={goBack} />,
        messages: <Chats setActiveTab={goBack} hideBottomBar={(isHidden) => setHideBottomBar(isHidden)} />,
        settings: <Settings setActiveTab={goBack} />,
        favorites: <Favorites setActiveTab={goBack} />,
        contractTemplates: <ContractTemplates setActiveTab={goBack} />,
        myReviews: <MyReviewsContent setActiveTab={goBack} />,
        counterpartyCheck: <CounterpartyCheck setActiveTab={goBack} />,
        finance: <Finance setActiveTab={goBack} />,
        dealerEmployees: <DealerEmployees setActiveTab={goBack} />,
        myOrders: <MyOrders setActiveTab={goBack} />
    };

    const tabNames = useMemo(() => ({
        walletTopUp: t('replenishWallet'),
        purchasePackage: t('buyPackage'),
        paymentHistory: t('paymentHistory'),
        ads: t('announcements'),
        messages: t('messages'),
        settings: t('settings'),
        favorites: t('favorites'),
        contractTemplates: t('contractTemplates'),
        myReviews: t('feedbacks'),
        counterpartyCheck: t('counterpartyCheck'),
        finance: t('finance'),
        dealerEmployees: t('employees'),
        myOrders: t('myOrders')
    }), [t]);

    useEffect(() => {
        const updateActiveTab = () => {
            const hash = window.location.hash.replace('#', '');
            if (tabNames[hash]) {
                setActiveTab(hash);
            } else {
                setActiveTab(null);
            }
        };
        
        // Initial load
        updateActiveTab();
        
        // Listen to hash changes
        const handleHashChange = () => {
            updateActiveTab();
        };
        
        window.addEventListener('hashchange', handleHashChange);
        
        // Also listen to location changes from React Router
        const hash = location.hash.replace('#', '');
        if (tabNames[hash]) {
            setActiveTab(hash);
        } else {
            setActiveTab(null);
        }
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [tabNames, location]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 786);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {(!isMobile || !activeTab) && <Header />}
            {(!isMobile || !activeTab) && (
                <Path
                    currentPath={`${t('profile')}/${tabNames[activeTab] || ''}`}
                    pathTo="/profile"
                />
            )}

            <div className="flex flex-col md:flex-row flex-1 gap-5 px-4 md:px-[60px] lg:px-[120px] xl:px-[200px] py-6 items-start">
                {(!isMobile || !activeTab) && <Menu setActiveTab={setActiveTab} />}
                {activeTab
                    ? <div className="flex-1 min-w-0 w-full pb-20 md:pb-6">{tabComponents[activeTab]}</div>
                    : null
                }
            </div>

            {!hideBottomBar && (
                <MobileBottomBar activeTabName={activeTab} setActiveTab={setActiveTab} />
            )}
        </div>
    );
}

export default Main;